// Shared utilities and content for /foodtech public website
// Distinct identity from /polyaurangabad — green + orange + white, food-tech focused.
import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export const FOOD_COLORS = {
  primary: "#1F6B3F",        // deep leaf green
  primaryDark: "#13532E",
  primaryLight: "#E6F3EB",
  accent: "#F4751F",         // turmeric orange
  accentDark: "#C95507",
  accentLight: "#FFF1E5",
  cream: "#FCFAF4",          // bg
  ink: "#0F1F17",            // text
  inkSoft: "#4D5A52",
};

export function slugify(name) {
  return String(name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function formatDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

export function PageHero({ eyebrow, title, subtitle, accent = "green" }) {
  const bg = accent === "orange"
    ? "from-[#F4751F] via-[#E36213] to-[#C95507]"
    : "from-[#1F6B3F] via-[#175030] to-[#0e3a22]";
  return (
    <section className={`relative bg-gradient-to-br ${bg} text-white py-16 md:py-24 overflow-hidden`} data-testid="ft-page-hero">
      <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
        backgroundImage: 'radial-gradient(circle at 25% 30%, white 1px, transparent 1.5px), radial-gradient(circle at 75% 60%, white 1px, transparent 1.5px)',
        backgroundSize: '40px 40px, 50px 50px',
      }} />
      <div className="relative max-w-7xl mx-auto px-4">
        {eyebrow && (
          <div className="text-[10px] tracking-[0.4em] uppercase font-semibold text-[#FFD9A8] mb-3">
            {eyebrow}
          </div>
        )}
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight max-w-4xl">{title}</h1>
        {subtitle && (
          <p className="mt-4 max-w-2xl text-white/85 text-base md:text-lg">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

export function PageBody({ children, className = "" }) {
  return (
    <section className={`max-w-7xl mx-auto px-4 py-12 md:py-16 ${className}`}>
      {children}
    </section>
  );
}

export function Lightbox({ photos, idx, onIdx, onClose }) {
  const cur = photos[idx];
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onIdx((idx - 1 + photos.length) % photos.length);
      if (e.key === "ArrowRight") onIdx((idx + 1) % photos.length);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [idx, photos.length, onClose, onIdx]);
  if (!cur) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ft-lightbox"
    >
      <button onClick={onClose} className="absolute top-5 right-5 text-white/80 hover:text-white p-2" aria-label="Close">
        <X size={22} />
      </button>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onIdx((idx - 1 + photos.length) % photos.length); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-sm"
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onIdx((idx + 1) % photos.length); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-sm"
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}
      <figure className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img src={cur.image_url || cur.url} alt={cur.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
        {cur.caption && <figcaption className="mt-3 text-center text-white/90 text-sm">{cur.caption}</figcaption>}
      </figure>
    </div>
  );
}

// Lightweight renderer for institute Vision/Mission/About content (HTML or plain text).
export function renderInstituteHtml(content) {
  if (!content) return null;
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
  return isHtml
    ? content
    : content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
}

// Static fallback copy used when CMS does not yet provide vision/mission/about.
export const STATIC_CONTENT = {
  about_intro:
    "MIT College of Food Technology is a flagship institution of Gramaudyogik Shikshan Mandal dedicated to advancing the science of food, nutrition, and processing. We blend rigorous classroom learning with hands-on laboratory practice, industry-driven projects, and an active incubation ecosystem — nurturing the next generation of food technologists, entrepreneurs, and researchers shaping how India eats.",
  vision:
    "To be a globally recognised centre of excellence in Food Technology education, research, and entrepreneurship — pioneering safe, nutritious, and sustainable food systems for an evolving world.",
  mission:
    "Deliver an industry-relevant curriculum that blends food science, processing engineering, and business; foster applied research that solves real problems for India's agri-food value chain; cultivate ethical, innovation-driven graduates equipped to lead in food companies, regulatory bodies, and their own ventures.",
};
